import {NotFoundPage, OnlineOfferEngagementTracker} from '@online-offer/shared';
import {ErrorBoundary} from '@sentry/react';
import {Show} from 'platform/foundation';
import {match} from 'ts-pattern';

import {Outlet, useParams} from 'react-router-dom';

import {any, isNil, isNotNil} from 'ramda';
import {isFalse, isNilOrEmpty, isTrue} from 'ramda-adjunct';

import {ErrorSection} from '../../(sections)/ErrorSection/ErrorSection';
import {Loading} from '../../(sections)/Loading/Loading';
import {useSendBuyingOnlineOfferEngagementMutation} from '../../api/buyingOnlineOfferApi';
import {useSendOnlineOfferEngagementMutation} from '../../api/onlineOfferApi';
import {queryParams} from '../../consts/queryParams';
import {useFinancialProducts} from '../../hooks/useFinancialProducts';
import {useGetOnlineOfferData} from '../../hooks/useGetOnlineOfferData';
import {getBuyingOnlineOfferEngagementEndpoint} from '../../utils/getBuyingOnlineOfferEngagementEndpoint';
import {getOnlineOfferEngagementEndpoint} from '../../utils/getOnlineOfferEngagementEndpoint';
import {getWorkspaceFromUri} from '../../utils/getWorkspaceFromUri';
import {useQueryState} from '../../utils/useQueryState';
import {ApplicationInitializeEvent} from '../ApplicationInitializeEvent/ApplicationInitializeEvent';
import {Button} from '../Button/Button';

const {workspace} = getWorkspaceFromUri();
export function ApplicationInitializeBoundary() {
  const params = useParams<{offerId: string}>();
  const [sellerEmail] = useQueryState(queryParams.SELLER_EMAIL);
  const {
    alphaCatalogue,
    vehicleAudit,
    direction,
    sessionId,
    isPdfRoute,
    isOfferLoaded,
    isOfferFetching,
    isOfferErrored,
    offerError,
    isAlphaCatalogueFetching,
    isAlphaCatalogueErrored,
    isVehicleAuditFetching,
    isVehicleAuditErrored,
    carAuditId,
    isListLogosFetching,
    isOfferAlternativesLoading,
    vehicle,
  } = useGetOnlineOfferData();
  const {
    financialProducts,
    address: financialProductsAddress,
    isFinancialProductsFetching,
    isFinancialProductsErrored,
  } = useFinancialProducts();
  const isBuying = direction === 'BUYING';

  // Print waits for a complete snapshot; the live page must not be blocked by its polled section.
  // Buying offers never request financial products and are settled immediately.
  const isFinancialProductsSettled =
    isNil(financialProductsAddress) || isNotNil(financialProducts) || isFinancialProductsErrored;

  const isFetching = any(isTrue, [
    isOfferFetching,
    isAlphaCatalogueFetching,
    isVehicleAuditFetching,
    isListLogosFetching,
    // Only an offer without a deal vehicle is drawn from its alternatives; any other offer renders
    // at once and gains the strip when they arrive. First load only, so a choice never blanks it.
    isOfferAlternativesLoading && isNil(vehicle),
    isPdfRoute && isFinancialProductsFetching,
  ]);

  const isErrored = any(isTrue, [isOfferErrored, isAlphaCatalogueErrored, isVehicleAuditErrored]);

  const isNotFoundError =
    isNotNil(offerError) &&
    'status' in offerError &&
    (offerError.status === 400 || offerError.status === 404);

  const isAppReady =
    isOfferLoaded &&
    isNotNil(alphaCatalogue) &&
    // Require loading of certificate only if certificate(carAuditId) is present
    (isNilOrEmpty(carAuditId) || isNotNil(vehicleAudit)) &&
    (!isPdfRoute || isFinancialProductsSettled);

  const engagementEndpoint = isBuying
    ? getBuyingOnlineOfferEngagementEndpoint(params.offerId ?? '')
    : getOnlineOfferEngagementEndpoint(params.offerId ?? '');
  const [sendSellingEngagement] = useSendOnlineOfferEngagementMutation();
  const [sendBuyingEngagement] = useSendBuyingOnlineOfferEngagementMutation();
  const sendEngagement = isBuying ? sendBuyingEngagement : sendSellingEngagement;

  const getComponent = () =>
    match({isNotFoundError, isErrored, isLoading: isFetching || isFalse(isAppReady)})
      .with({isNotFoundError: true}, () => (
        <NotFoundPage sellerEmail={sellerEmail} ContactSellerButton={Button} />
      ))
      .with({isErrored: true}, () => <ErrorSection />)
      .with({isLoading: true}, () => <Loading />)
      .otherwise(() => <Outlet />);

  return (
    <>
      <Show when={isAppReady && process.env.ENV_TYPE === 'prod'}>
        <ApplicationInitializeEvent
          key={params.offerId}
          offerId={params.offerId}
          workspace={workspace}
        />
      </Show>
      <Show when={isAppReady && isNotNil(sessionId) && !isPdfRoute}>
        <OnlineOfferEngagementTracker
          key={`${params.offerId}-${direction}`}
          offerId={params.offerId ?? ''}
          workspace={workspace ?? ''}
          sessionId={sessionId ?? ''}
          engagementEndpoint={engagementEndpoint}
          sendEngagement={sendEngagement}
        />
      </Show>
      {/* Both directions render the same page element; keying per offer drops the
          previous offer's local state (e.g. Sidebar timers) and resets the boundary. */}
      <ErrorBoundary key={`${params.offerId}-${direction}`} fallback={<ErrorSection />}>
        {getComponent()}
      </ErrorBoundary>
    </>
  );
}
